import axios from 'axios';
import { getToken } from '../../redux/reducers/user/user';
import { ensureTrailingSlash } from '../url/url';

const apiUrl = ensureTrailingSlash(process.env.REACT_APP_API_URL);
const apiPrefix = 'api/v1/';

const createConfig = (accessToken = null) => {
  return {
    headers: createAuthHeader(accessToken),
  };
};

const createAuthHeader = (accessToken) => {
  return {
    'content-type': 'application/json',
    'Authorization': accessToken != null ? `Bearer ${accessToken}` : null,
    'x-chabla-api-key': process.env.REACT_APP_API_KEY,
  };
};

const doGetToken = async (path, body) => {
  try {
    const response = await axios.post(path, body, createConfig('no_token_needed'));
    return response.data;
  } catch (error) {
    throwError(error);
  }
};

const Rest = {
  init: (dispatch) => {
    // Request interceptor for API calls
    axios.interceptors.request.use(
      async (config) => {
        // add token to the request
        const currentHeaders = config.headers;
        if (currentHeaders.Authorization == null) {
          const token = await dispatch(getToken());
          const authHeader = createAuthHeader(token);
          config.headers = authHeader;
        }
        return config;
      },
      (error) => {
        Promise.reject(error);
      }
    );
  },
  getToken: async (path, username, password) => {
    const data = {
      username,
      password,
      grant_type: 'password',
      web: true,
    };
    return await doGetToken(path, data);
  },
  renewToken: async (path, refreshToken) => {
    const data = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token',
    };
    return await doGetToken(path, data);
  },
  get: async (path, props) => {
    try {
      const response = await axios.get(path, { ...createConfig(), ...props });
      return response.data;
    } catch (error) {
      throwError(error);
    }
  },
  post: async (path, data) => {
    try {
      const response = await axios.post(path, data, createConfig());
      return response.data;
    } catch (error) {
      throwError(error);
    }
  },
  put: async (path, data) => {
    try {
      const response = await axios.put(path, data, createConfig());
      return response.data;
    } catch (error) {
      throwError(error);
    }
  },
  delete: async (path) => {
    try {
      const response = await axios.delete(path, createConfig());
      return response.data;
    } catch (error) {
      throwError(error);
    }
  },
};

const throwError = (error) => {
  const newError = new Error();
  if (error.response) {
    // server responded with error
    const { status, statusText } = error.response;
    newError.status = status;
    newError.message = statusText;
  } else if (error.request) {
    // The request was made but no response was received
    newError.status = 520;
    newError.message = error.message;
  } else {
    // Something happened in setting up the request that triggered an Error
    newError.status = 520;
    newError.message = error.message;
  }

  throw newError;
};

export default Rest;

export const NoAuthRest = axios.create({
  baseURL: apiUrl + apiPrefix,
  timeout: 5000,
});

NoAuthRest.interceptors.request.use(async (config) => {
  config.headers['x-chabla-api-key'] = process.env.REACT_APP_API_KEY;
  return config;
});

NoAuthRest.interceptors.response.use(null, (error) => {
  const newError = new Error();
  if (error.response) {
    // server responded with error
    const { status, statusText } = error.response;
    newError.status = status;
    newError.message = statusText;
  } else if (error.request) {
    // The request was made but no response was received
    newError.status = 520;
    newError.message = error.message;
  } else {
    // Something happened in setting up the request that triggered an Error
    newError.status = 520;
    newError.message = error.message;
  }
  throw newError;
});
